import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Checkbox, Switch, Tooltip } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import AnnualReportModal from '../../../AnnualReport/AnnualReportModal';
import modalStyles from '../../../AnnualReport/AnnualReportModal.module.scss';
import AnnualReportService from '../../../../Services/annualReport.service';
import { formatAmount, translateServerError } from '../../../AnnualReport/annualReportUtils';
import { FORM_1343_FIRST_YEAR, FORM_1343_STATUS, FORM_1343_STATUS_KEYS, exportForm1343Excel } from './form1343';
import styles from './Form1343Modal.module.scss';

const formatDate = (value) => (value ? dayjs(value).format('DD/MM/YYYY') : '');
const formatCoefficient = (value) => (value == null ? '—' : Number(value).toFixed(4));

/** טופס 1343 – ניכוי נוסף בשל פחת: per-asset calculation for one tax year. */
const Form1343Modal = ({ open, onClose, client, token, year: initialYear, direction = 'rtl', onChanged }) => {
    const { t } = useTranslation();
    const maxYear = new Date().getFullYear() + 1;
    const [year, setYear] = useState(initialYear);
    const [showAll, setShowAll] = useState(false);
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [savingId, setSavingId] = useState(null);

    useEffect(() => {
        if (open) setYear(Math.max(FORM_1343_FIRST_YEAR, Math.min(initialYear, maxYear)));
    }, [open, initialYear, maxYear]);

    const load = useCallback(async () => {
        if (!client?.caseId || !token || !year) return;
        setLoading(true);
        setError('');
        try {
            setReport(await AnnualReportService.getForm1343(client.caseId, year, token));
        } catch (loadError) {
            setReport(null);
            setError(translateServerError(t, loadError));
        } finally {
            setLoading(false);
        }
    }, [client?.caseId, token, year, t]);

    useEffect(() => {
        if (open) load();
    }, [open, load]);

    const toggleExcluded = async (row) => {
        setSavingId(row.assetId);
        try {
            await AnnualReportService.setForm1343Exclusion(client.caseId, row.assetId, !row.excluded, token);
            await load();
            onChanged?.();
        } catch (saveError) {
            setError(translateServerError(t, saveError));
        } finally {
            setSavingId(null);
        }
    };

    const rows = useMemo(() => {
        const all = report?.rows || [];
        return showAll ? all : all.filter((row) => row.status !== FORM_1343_STATUS.PurchasedAfter2007);
    }, [report, showAll]);

    const years = useMemo(() => {
        const list = [];
        for (let y = maxYear; y >= FORM_1343_FIRST_YEAR; y -= 1) list.push(y);
        return list;
    }, [maxYear]);

    const footer = (
        <>
            <span className={modalStyles.footerMeta}>
                {report && t('form1343.summary', { count: report.eligibleCount, total: formatAmount(report.totalAdditionalDeduction) })}
            </span>
            <button
                type="button"
                className={modalStyles.secondaryBtn}
                disabled={!report || loading}
                onClick={() => exportForm1343Excel({ client, report, t, direction })}
            >
                <FileDownloadOutlinedIcon fontSize="small" />
                {t('form1343.exportExcel')}
            </button>
            <button type="button" className={modalStyles.primaryBtn} onClick={onClose}>
                {t('Close') || 'Close'}
            </button>
        </>
    );

    return (
        <AnnualReportModal
            open={open}
            onClose={onClose}
            direction={direction}
            wide
            title={(
                <>
                    {t('form1343.title')}
                    <span className={modalStyles.formCode}>1343</span>
                </>
            )}
            footer={footer}
        >
            <div className={styles.toolbar}>
                <label className={styles.yearField}>
                    <span>{t('form1343.taxYear')}</span>
                    <select className={modalStyles.selectInput} value={year} onChange={(e) => setYear(Number(e.target.value))}>
                        {years.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                </label>
                <label className={styles.switchField}>
                    <Switch size="small" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
                    <span>{t('form1343.showAll')}</span>
                </label>
            </div>

            <p className={modalStyles.muted}>{t('form1343.explanation')}</p>
            {error && <p className={modalStyles.warning}>{error}</p>}
            {report?.missingCoefficientCount > 0 && (
                <p className={modalStyles.warning}>{t('form1343.missingCoefficientWarning', { count: report.missingCoefficientCount })}</p>
            )}

            {loading && <div className={modalStyles.loading}>{t('Loading') || '...'}</div>}

            {!loading && report && (
                rows.length === 0 ? (
                    <div className={modalStyles.emptyState}>{t('form1343.empty')}</div>
                ) : (
                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>{t('form1343.columns.include')}</th>
                                    <th>{t('form1343.columns.asset')}</th>
                                    <th>{t('form1343.columns.group')}</th>
                                    <th>{t('form1343.columns.originalPrice')}</th>
                                    <th>{t('form1343.columns.purchaseDate')}</th>
                                    <th>{t('form1343.columns.depreciation')}</th>
                                    <th>{t('form1343.columns.coefficient')}</th>
                                    <th>{t('form1343.columns.additionalDeduction')}</th>
                                    <th>{t('form1343.columns.status')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => {
                                    const eligibleByDate = row.status !== FORM_1343_STATUS.PurchasedAfter2007;
                                    const eligible = row.status === FORM_1343_STATUS.Eligible;
                                    return (
                                        <tr key={row.assetId} className={eligible ? '' : styles.rowMuted}>
                                            <td>
                                                {eligibleByDate && (
                                                    <Tooltip title={t('form1343.includeHint')}>
                                                        <span>
                                                            <Checkbox
                                                                size="small"
                                                                checked={!row.excluded}
                                                                disabled={savingId === row.assetId}
                                                                onChange={() => toggleExcluded(row)}
                                                            />
                                                        </span>
                                                    </Tooltip>
                                                )}
                                            </td>
                                            <td className={styles.assetCell}>
                                                <span className={styles.assetNumber}>{row.assetNumber}</span>
                                                <span>{row.name}</span>
                                            </td>
                                            <td>{row.assetGroupName || t('form1343.noGroup')}</td>
                                            <td className={styles.num}>{formatAmount(row.originalPrice)}</td>
                                            <td>{formatDate(row.purchaseDate)}</td>
                                            <td className={styles.num}>{formatAmount(row.depreciationThisYear)}</td>
                                            <td className={styles.num}>
                                                {formatCoefficient(row.coefficient)}
                                                {row.coefficientSource === 1 && <span className={styles.cpiTag}>{t('form1343.fromCpi')}</span>}
                                            </td>
                                            <td className={`${styles.num} ${eligible ? styles.deduction : ''}`}>
                                                {eligible ? formatAmount(row.additionalDeduction) : '—'}
                                            </td>
                                            <td>
                                                <span className={`${styles.status} ${styles[`status${row.status}`] || ''}`}>
                                                    {t(`form1343.status.${FORM_1343_STATUS_KEYS[row.status]}`)}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan={5}>{t('form1343.totals')}</td>
                                    <td className={styles.num}>{formatAmount(report.eligibleDepreciation)}</td>
                                    <td />
                                    <td className={`${styles.num} ${styles.deduction}`}>{formatAmount(report.totalAdditionalDeduction)}</td>
                                    <td />
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                )
            )}
        </AnnualReportModal>
    );
};

Form1343Modal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    client: PropTypes.shape({ caseId: PropTypes.string, name: PropTypes.string }),
    token: PropTypes.string,
    year: PropTypes.number.isRequired,
    direction: PropTypes.oneOf(['rtl', 'ltr']),
    onChanged: PropTypes.func,
};

export default Form1343Modal;
